// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@insite/content-library/Components/CustomerAddressFormFields";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import { ToastPresentationProps } from "@insite/mobius/Toast";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resetOverrideAddressFormValidation from "@mcmc/App/Handlers/Pages/CheckoutShipping/ResetOverrideAddressFormValidation";
import setNextCheckoutStep from "@mcmc/App/Handlers/Pages/CheckoutShipping/SetNextCheckoutStep";
import setOverrideShipTo from "@mcmc/App/Handlers/Pages/CheckoutShipping/SetOverrideShipTo";
import validateOverrideAddressForm from "@mcmc/App/Handlers/Pages/CheckoutShipping/ValidateOverrideAddressForm";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import SuggestedAddressesModal from "@mcmc/App/Widgets/CheckoutShipping/SuggestedAddressesModal";
import Notify from "@mcmc/XDL/Components/Notify";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: ShippingAddressFormStyles;
}

const mapStateToProps = ({
    xCustomCheckoutShipping: { overrideAddressFormState, showValidationModal, validateAddressResult },
}: McMcApplicationState) => ({
    overrideAddressFormState,
    showValidationModal,
    validationErrors: validateAddressResult?.errorMessages,
});

const mapDispatchToProps = {
    setAddress: (address: ShipToModel | undefined) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "CustomCheckoutShipping/SetOverrideAddress",
            address,
        } as any);
    },
    validateForm: makeHandlerChainAwaitable<Parameters<typeof validateOverrideAddressForm>[0], boolean>(
        validateOverrideAddressForm,
    ),
    resetOverrideAddressFormValidation,
    setOverrideShipTo,
    setNextCheckoutStep,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

type State = {
    styles: ShippingAddressFormStyles;
};

export interface ShippingAddressFormStyles {
    formFields?: CustomerAddressFormFieldStyles;
    buttonsWrapper?: InjectableCss;
    continueButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    toastStyles?: ToastPresentationProps;
}

export const overrideAddressFormStyles: ShippingAddressFormStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            margin-top: 30px;
        `,
    },
    continueButton: {
        variant: "primary",
        css: css`
            margin-right: 10px;
        `,
    },
    cancelButton: { variant: "secondary" },
    toastStyles: {
        cssOverrides: {
            toast: css`
                & {
                    margin: 30px 0;
                    width: 100%;
                }
            `,
        },
    },
};

const styles = overrideAddressFormStyles;

class OverrideAddressForm extends React.Component<Props, State> {
    static contextType = CheckoutShippingFormContext;

    context!: React.ContextType<typeof CheckoutShippingFormContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            styles: mergeToNew(styles, this.props.extendedStyles),
        };
    }

    componentDidMount() {
        const { overrideAddressFormState, fieldDisplay, validateForm } = this.props;
        if (!overrideAddressFormState) {
            return;
        }
        const { address } = overrideAddressFormState;
        const { validation } = address;
        if (!validation) {
            return;
        }

        const shouldValidate =
            validation.firstName?.isDisabled === false ||
            validation.lastName?.isDisabled === false ||
            validation.companyName?.isDisabled === false ||
            validation.attention?.isDisabled === false ||
            validation.address1?.isDisabled === false ||
            validation.address2?.isDisabled === false ||
            validation.address3?.isDisabled === false ||
            validation.address4?.isDisabled === false ||
            validation.city?.isDisabled === false ||
            validation.state?.isDisabled === false ||
            validation.country?.isDisabled === false ||
            validation.postalCode?.isDisabled === false ||
            validation.phone?.isDisabled === false ||
            validation.email?.isDisabled === false ||
            validation.fax?.isDisabled === false;
        if (shouldValidate) {
            this.context.validators.overrideAddress = () =>
                validateForm({
                    address,
                    validation,
                    fieldDisplay,
                });
            this.props.resetOverrideAddressFormValidation();
        }
        this.props.setOverrideShipTo({ overrideShipTo: true });
    }

    componentWillUnmount() {
        this.context.validators.overrideAddress = undefined;
        this.props.setOverrideShipTo({ overrideShipTo: false });
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { overrideAddressFormState: prevShippingAddressFormState, fieldDisplay: prevFieldDisplay } = prevProps;
        const { overrideAddressFormState, fieldDisplay, validateForm } = this.props;
        if (prevShippingAddressFormState !== overrideAddressFormState && overrideAddressFormState) {
            const { address } = overrideAddressFormState;
            const { validation } = address;
            if (
                (!prevShippingAddressFormState ||
                    prevShippingAddressFormState.address !== address ||
                    prevShippingAddressFormState.address.validation !== validation ||
                    prevFieldDisplay !== fieldDisplay) &&
                validation
            ) {
                const shouldValidate =
                    validation.firstName?.isDisabled === false ||
                    validation.lastName?.isDisabled === false ||
                    validation.companyName?.isDisabled === false ||
                    validation.attention?.isDisabled === false ||
                    validation.address1?.isDisabled === false ||
                    validation.address2?.isDisabled === false ||
                    validation.address3?.isDisabled === false ||
                    validation.address4?.isDisabled === false ||
                    validation.city?.isDisabled === false ||
                    validation.state?.isDisabled === false ||
                    validation.country?.isDisabled === false ||
                    validation.postalCode?.isDisabled === false ||
                    validation.phone?.isDisabled === false ||
                    validation.email?.isDisabled === false ||
                    validation.fax?.isDisabled === false;
                if (shouldValidate) {
                    this.context.validators.overrideAddress = () =>
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });

                    if (this.context.formSubmitAttempted) {
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });
                    }
                } else {
                    this.context.validators.overrideAddress = undefined;
                }
            }
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { overrideAddressFormState, setAddress } = this.props;
        if (!overrideAddressFormState) {
            return;
        }
        const { address } = overrideAddressFormState;
        setAddress({
            ...address,
            [event.target.name]: event.target.value,
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const { overrideAddressFormState, setAddress } = this.props;
        if (!overrideAddressFormState) {
            return;
        }
        const { address } = overrideAddressFormState;
        setAddress({
            ...address,
            country,
            postalCode: "",
            state: null,
        });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        const { overrideAddressFormState, setAddress } = this.props;
        if (!overrideAddressFormState) {
            return;
        }
        const { address } = overrideAddressFormState;
        setAddress({
            ...address,
            state,
        });
    };

    onContinueClick = () => {
        this.props.setNextCheckoutStep({ stepHash: "" });
    };

    render() {
        const { overrideAddressFormState, countries, fieldDisplay, showValidationModal, validationErrors } = this.props;
        const { styles } = this.state;
        if (!overrideAddressFormState) {
            return null;
        }
        const { address, formErrors } = overrideAddressFormState;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
            validation,
        } = address;
        if (!validation) {
            return null;
        }

        return (
            <StyledWrapper>
                <CustomerAddressFormFields
                    firstName={firstName}
                    lastName={lastName}
                    attention={attention}
                    companyName={companyName}
                    address1={address1}
                    address2={address2}
                    address3={address3}
                    address4={address4}
                    country={country}
                    city={city}
                    state={state}
                    postalCode={postalCode}
                    phone={phone}
                    email={email}
                    fax={fax}
                    onChangeFirstName={this.handleInputChange}
                    onChangeLastName={this.handleInputChange}
                    onChangeAttention={this.handleInputChange}
                    onChangeCompanyName={this.handleInputChange}
                    onChangeAddress1={this.handleInputChange}
                    onChangeAddress2={this.handleInputChange}
                    onChangeAddress3={this.handleInputChange}
                    onChangeAddress4={this.handleInputChange}
                    onChangeCountry={this.handleCountryChange}
                    onChangeState={this.handleStateChange}
                    onChangeCity={this.handleInputChange}
                    onChangePostalCode={this.handleInputChange}
                    onChangePhone={this.handleInputChange}
                    onChangeEmail={this.handleInputChange}
                    onChangeFax={this.handleInputChange}
                    firstNameError={formErrors.firstName}
                    lastNameError={formErrors.lastName}
                    attentionError={formErrors.attention}
                    companyNameError={formErrors.companyName}
                    address1Error={formErrors.address1}
                    address2Error={formErrors.address2}
                    address3Error={formErrors.address3}
                    address4Error={formErrors.address4}
                    countryError={formErrors.country}
                    stateError={formErrors.state}
                    cityError={formErrors.city}
                    postalCodeError={formErrors.postalCode}
                    phoneError={formErrors.phone}
                    emailError={formErrors.email}
                    faxError={formErrors.fax}
                    countries={countries}
                    validation={validation}
                    fieldDisplay={fieldDisplay}
                    extendedStyles={styles.formFields}
                />
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button type="submit" {...styles.continueButton} onClick={() => this.onContinueClick()}>
                        {translate("Continue")}
                    </Button>
                    <Button
                        type="button"
                        {...styles.cancelButton}
                        onClick={() => {
                            this.props.setOverrideShipTo({ overrideShipTo: false });
                            this.props.setAddress(undefined);
                        }}
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
                {validationErrors?.map((message, index) => (
                    <Notify messageType="danger" body={message} extendedStyles={styles.toastStyles} key={index} />
                ))}
                <SuggestedAddressesModal isOpen={showValidationModal} />
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(OverrideAddressForm);
